// SuitabilityCard (TASK-579 Phase 4) — compact FinSA/MiFID suitability verdict for one
// proposal against the client's REAL stored risk profile. Self-fetches by injected
// clientId/proposalId via the compliance analyze-proposal engine (Phase 2 made it real).
// Distinct from the full RiskProfileTab: this is the aligned/breach verdict + reasons.
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { analyzeProposal } from '../../../services/complianceActivityService';
import { WidgetContainer } from '../WidgetContainer';

const BREACH_FORMS = new Set(['risk-profile-override', 'risk-profile-required']);

export function SuitabilityCard({ clientId, proposalId }: { clientId: string; proposalId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['suitability', proposalId, clientId],
    queryFn: () => analyzeProposal(proposalId, clientId),
    enabled: !!proposalId,
  });

  const breaches = (data?.required_forms ?? []).filter((f) => BREACH_FORMS.has(f.form_id));
  const missing = breaches.some((f) => f.form_id === 'risk-profile-required');
  const aligned = !!data?.is_compliant && breaches.length === 0;

  return (
    <WidgetContainer source="Compliance · Suitability">
      {isLoading && (
        <div className="space-y-2">
          <div className="h-5 w-40 animate-pulse rounded bg-accent" />
          <div className="h-16 w-full animate-pulse rounded bg-accent" />
        </div>
      )}

      {isError && (
        <div className="text-[13px] text-muted-foreground">
          Could not check suitability. {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {data && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            {aligned ? (
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <ShieldAlert className="h-5 w-5 text-destructive" />
            )}
            <span className="text-[15px] font-semibold text-foreground">
              {missing ? 'Profile missing' : aligned ? 'Suitable' : 'Suitability breach'}
            </span>
            <span className="ml-auto text-[12px] text-muted-foreground">
              {Math.round((data.compliance_score ?? 0) * 100)}%
            </span>
          </div>

          {missing && (
            <p className="text-[13px] text-amber-700">
              No determined risk profile on file — suitability cannot be assessed. Complete the
              risk profile before recommending.
            </p>
          )}

          {!missing && breaches.length > 0 && (
            <ul className="space-y-1">
              {breaches.map((f) => (
                <li key={f.form_id} className="flex items-center gap-2 text-[13px] text-destructive">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  <span className="font-medium">{f.form_name}</span>
                  <span className="text-[11px] text-muted-foreground">{f.regulation}</span>
                </li>
              ))}
            </ul>
          )}

          {!missing && aligned && (
            <p className="text-[13px] text-muted-foreground">
              This proposal aligns with the client's risk profile. No suitability breach detected.
            </p>
          )}

          {(data.warnings ?? []).map((w, i) => (
            <div key={i} className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              {w}
            </div>
          ))}

          <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground/70">
            Source: FinSA/MiFID suitability engine · client's stored profile
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}
